import { inject, Injectable, OnDestroy, signal, Signal, WritableSignal } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';
import { SubscriptionMessage, WebSocketResponse } from '../models/websocket-types';
import { BaseIntegrationSystem } from '../pages/create/base-item-creation.component';
import { ErrorService } from './error.service';
import { NotificationService } from './notification.service';
import { TranslateService } from '@ngx-translate/core';
import { StepperNavigationService } from './stepper-navigation.service';
import { WebSocketSubscriptionsService } from './websocket/websocket-subscriptions.service';
import { WebSocketService } from './websocket/websocket.service';

export type IntegrationConfig = {
  name: string;
  topic: SubscriptionMessage['topic'];
  step: number;
};

type IntegrationStatus = 'pending' | 'in-progress' | 'completed' | 'failed';

// Constants for step numbers and status keys
const FIRST_STEP = 1;
const INTEGRATION_PENDING = 'CREATE.INTEGRATION_PENDING';
const INTEGRATION_IN_PROGRESS = 'CREATE.INTEGRATION_IN_PROGRESS';
const INTEGRATION_COMPLETED = 'CREATE.INTEGRATION_COMPLETED';
const INTEGRATION_FAILED = 'CREATE.INTEGRATION_FAILED';
const INTEGRATION_FAILED_CRITICAL = 'CREATE.INTEGRATION_FAILED_CRITICAL';

// Define type for the primary data expected from a Step 1 integration
export type PrimaryIntegrationData = {
  id: string;
  [key: string]: unknown;
};

@Injectable({
  providedIn: 'root'
})
export class IntegrationManagerService implements OnDestroy {
  private integrationSystemsSignal: WritableSignal<BaseIntegrationSystem[]> = signal([]);
  private primaryItemIdSignal: WritableSignal<string | undefined> = signal(undefined);
  private destroy$ = new Subject<void>();
  private allIntegrationConfigs: IntegrationConfig[] = [];
  private activeSubscriptions = new Map<string, { subscription: Subscription; topic: string; destroy$: Subject<void> }>();
  private preSubscribedTopics = new Set<SubscriptionMessage['topic']>();

  private integrationWebSocket = inject(WebSocketSubscriptionsService);
  private webSocketService = inject(WebSocketService);
  private errorService = inject(ErrorService);
  private notificationService = inject(NotificationService);
  private translate = inject(TranslateService);
  private stepperNavigation = inject(StepperNavigationService);

  public setupIntegrationCompletionListenersForStep(
    integrationConfigs: IntegrationConfig[],
    primaryItemId: string | undefined,
    step: number,
    onStep1Data?: (data: PrimaryIntegrationData) => void
  ): void {
    if (step === FIRST_STEP) {
      this.resetIntegrationState();
    }

    this.allIntegrationConfigs = integrationConfigs;
    this.cleanupSubscriptionsForStep(step, integrationConfigs);

    const stepIntegrations = integrationConfigs.filter(ic => ic.step === step);
    this.initializeIntegrationStatuses(stepIntegrations, integrationConfigs);

    if (step === FIRST_STEP) {
      this.handleStep1Start(stepIntegrations, integrationConfigs, onStep1Data);
    } else {
      this.handleRegularStepStart(stepIntegrations, primaryItemId, step, integrationConfigs);
    }
  }

  private handleStep1Start(stepIntegrations: IntegrationConfig[], allConfigs: IntegrationConfig[], onStep1Data?: (data: PrimaryIntegrationData) => void): void {
    const primaryConfig = stepIntegrations[0];
    if (!primaryConfig) {
      console.warn('No primary data provider for step 1.');
      this.notifyStepCompletion(FIRST_STEP, allConfigs);
      return;
    }

    this.setupSingleIntegrationListener(primaryConfig, response => {
      const primaryData = this.extractPrimaryData(response);
      if (primaryData && onStep1Data) onStep1Data(primaryData);

      const others = stepIntegrations.slice(1);
      if (others.length > 0) {
        this.setupParallelIntegrationListeners(others, FIRST_STEP, allConfigs);
      } else {
        this.notifyStepCompletion(FIRST_STEP, allConfigs);
      }
    });
  }

  private handleRegularStepStart(
    stepIntegrations: IntegrationConfig[],
    primaryItemId: string | undefined,
    step: number,
    allConfigs: IntegrationConfig[]
  ): void {
    if (!primaryItemId) {
      console.error(`primaryItemId missing for step > ${FIRST_STEP}.`);
      this.errorService.showError(this.translate.instant('NOTIFICATIONS.ERROR.INTEGRATION_PRIMARY_ID_MISSING', { step }));
      stepIntegrations.forEach(config => this.updateIntegrationStatus(config.name, 'failed', INTEGRATION_FAILED_CRITICAL, true));
      this.notifyStepCompletion(step, allConfigs);
      return;
    }

    this.setupParallelIntegrationListeners(stepIntegrations, step, allConfigs);
  }

  private extractPrimaryData(response?: PrimaryIntegrationData | WebSocketResponse): PrimaryIntegrationData | undefined {
    if (!response || typeof response !== 'object' || !('id' in response) || typeof (response as PrimaryIntegrationData).id !== 'string') return undefined;
    return response as PrimaryIntegrationData;
  }

  private initializeIntegrationStatuses(stepIntegrations: IntegrationConfig[], allConfigs: IntegrationConfig[]): void {
    if (this.integrationSystemsSignal().length === 0 && allConfigs.length > 0) {
      this.integrationSystemsSignal.set(
        allConfigs.map((config: IntegrationConfig) => ({
          name: config.name,
          status: 'pending' as IntegrationStatus,
          statusText: INTEGRATION_PENDING
        }))
      );
    }

    // Update statuses for current step integrations only
    if (stepIntegrations.length > 0) {
      this.integrationSystemsSignal.update(systems =>
        systems.map(system => {
          const stepIntegrationConfig = stepIntegrations.find(si => si.name === system.name);
          if (stepIntegrationConfig && system.status !== 'completed' && system.status !== 'failed') {
            return { ...system, status: 'pending' as IntegrationStatus, statusText: INTEGRATION_PENDING };
          }
          return system;
        })
      );
    }
  }

  private updateIntegrationStatus(name: string, status: IntegrationStatus, statusTextKey: string, error = false): void {
    this.integrationSystemsSignal.update(systems =>
      systems.map(sys => {
        if (sys.name.toLowerCase() === name.toLowerCase()) {
          return { ...sys, status, statusText: statusTextKey, error };
        }
        return sys;
      })
    );
  }

  private setupParallelIntegrationListeners(integrations: IntegrationConfig[], step: number, allConfigs: IntegrationConfig[]): void {
    integrations.forEach(integration => {
      this.setupSingleIntegrationListener(integration, () => {
        this.notifyStepCompletion(step, allConfigs);
      });
    });
  }

  private setupSingleIntegrationListener(integration: IntegrationConfig, onAnyCompletion?: (data?: PrimaryIntegrationData | WebSocketResponse) => void): void {
    this.updateIntegrationStatus(integration.name, 'in-progress', INTEGRATION_IN_PROGRESS);

    // Map integration names to correct message types
    const messageType = this.getMessageTypeForIntegration(integration.name);
    const messageSource = this.getMessageSourceForIntegration(integration.name);

    // Set up subscription to listen for integration completion notifications
    // Topics are pre-subscribed upfront, so we can set up handlers for all integrations
    const integrationDestroy$ = new Subject<void>();
    this.destroy$.pipe(takeUntil(integrationDestroy$)).subscribe(() => {
      integrationDestroy$.next();
      integrationDestroy$.complete();
    });
    const subscriptionObservable = this.integrationWebSocket.getIntegrationMessages(integration.topic, messageType, messageSource, integrationDestroy$).pipe(
      tap(message => {
        this.handleIntegrationMessage(message, integration, onAnyCompletion);
        integrationDestroy$.next();
        integrationDestroy$.complete();
      })
    );

    // Subscribe to the observable directly since it handles message processing internally
    const subscription = subscriptionObservable.subscribe();
    const subscriptionKey = `${integration.name}_${integration.step}`;

    // Store the subscription for cleanup
    this.activeSubscriptions.set(subscriptionKey, {
      subscription,
      topic: integration.topic,
      destroy$: integrationDestroy$
    });
  }

  private handleIntegrationMessage(
    message: WebSocketResponse,
    integration: IntegrationConfig,
    onAnyCompletion?: (data?: PrimaryIntegrationData | WebSocketResponse) => void
  ): void {
    const statusUpdate = this.createStatusUpdate(message.status === 'error');
    this.updateIntegrationStatus(integration.name, statusUpdate.status, statusUpdate.statusTextKey, statusUpdate.error);

    let callbackData: PrimaryIntegrationData | WebSocketResponse | undefined = message;

    if (message.status === 'success' && integration.step === FIRST_STEP) {
      const extractedPrimaryData = this.extractPrimaryDataFromMessage(message, integration);
      if (extractedPrimaryData) {
        callbackData = extractedPrimaryData;
        // Update central primary item id signal so UI components can react to it
        try {
          this.primaryItemIdSignal.set(extractedPrimaryData.id);
        } catch (e) {
          console.warn('[Integration Manager] Failed to set primaryItemIdSignal', e);
        }
      } else {
        console.warn(`[Integration Manager] Could not extract PrimaryIntegrationData from ${integration.name} response for step 1. Passing original message.`);
      }
    }

    if (onAnyCompletion) {
      onAnyCompletion(callbackData);
    }
  }

  private createStatusUpdate(isError: boolean): { status: IntegrationStatus; statusTextKey: string; error: boolean } {
    return {
      status: isError ? 'failed' : 'completed',
      statusTextKey: isError ? INTEGRATION_FAILED : INTEGRATION_COMPLETED,
      error: isError
    };
  }

  private extractPrimaryDataFromMessage(message: WebSocketResponse, _integration: IntegrationConfig): PrimaryIntegrationData | undefined {
    const dataAsUnknown = message.data as unknown;

    if (Array.isArray(dataAsUnknown) && dataAsUnknown.length > 0) {
      return this.findPrimaryDataInArray(dataAsUnknown);
    } else if (typeof dataAsUnknown === 'object' && dataAsUnknown !== null) {
      return this.findPrimaryDataInObject(dataAsUnknown as Record<string, unknown>);
    }

    return undefined;
  }

  private findPrimaryDataInArray(data: unknown[]): PrimaryIntegrationData | undefined {
    const firstEntry = data[0];
    if (typeof firstEntry === 'object' && firstEntry !== null) {
      const entryAsRecord = firstEntry as Record<string, unknown>;

      // Check for organization data in the entry
      const organizationData = entryAsRecord['organization'];
      if (this.isWellFormedPrimaryData(organizationData)) {
        return organizationData;
      }

      // Check if the entry itself is primary data
      if (this.isWellFormedPrimaryData(firstEntry)) {
        return firstEntry;
      }
    }
    return undefined;
  }

  private findPrimaryDataInObject(data: Record<string, unknown>): PrimaryIntegrationData | undefined {
    // Check if the data object itself is primary data
    if (this.isWellFormedPrimaryData(data)) {
      return data;
    }

    // Check for organization data within the object
    const organizationData = data['organization'];
    if (this.isWellFormedPrimaryData(organizationData)) {
      return organizationData;
    }

    return undefined;
  }

  private isWellFormedPrimaryData(obj: unknown): obj is PrimaryIntegrationData {
    return typeof obj === 'object' && obj !== null && typeof (obj as PrimaryIntegrationData).id === 'string';
  }

  private getMessageTypeForIntegration(integrationName: string): string {
    const typeMap: Record<string, string> = {
      HubSpot: 'hubspot-notification',
      Netvisor: 'netvisor-notification',
      HJC: 'hjc-notification',
      'Helos 2.0': 'helos-notification'
    };
    return typeMap[integrationName] || `${integrationName.toLowerCase()}-notification`;
  }

  private getMessageSourceForIntegration(integrationName: string): string {
    const sourceMap: Record<string, string> = {
      HubSpot: 'hubspot',
      Netvisor: 'netvisor',
      HJC: 'hjc',
      'Helos 2.0': 'helos'
    };
    return sourceMap[integrationName] || integrationName.toLowerCase();
  }

  /**
   * Checks if all integrations for the given step are completed or failed,
   * then updates the stepper navigation and shows a summary notification.
   */
  private notifyStepCompletion(step: number, allConfigs: IntegrationConfig[]): void {
    const systems = this.integrationSystemsSignal();
    const integrationsForThisStep = allConfigs.filter(config => config.step === step);

    if (integrationsForThisStep.length === 0 && step > 0) {
      // step > 0 because step 0 doesn't exist in 1-based config
      this.stepperNavigation.setStepCompletion(step - 1, true);
      return;
    }

    const relevantSystems = systems.filter(system => integrationsForThisStep.some(config => config.name.toLowerCase() === system.name.toLowerCase()));

    if (relevantSystems.length < integrationsForThisStep.length) {
      console.warn(
        `[Integration Manager] Mismatch: ${relevantSystems.length} systems found vs ${integrationsForThisStep.length} configured. Step check may be premature.`
      );
      // Not all systems configured for this step have a status in our signal yet,
      // or they haven't been initialized. This can happen if initializeIntegrationStatuses
      // wasn't comprehensive or if this check is called too early.
      return;
    }

    const allProcessed = relevantSystems.every(system => system.status === 'completed' || system.status === 'failed');

    if (allProcessed) {
      this.stepperNavigation.setStepCompletion(step - 1, true);

      const failedCount = relevantSystems.filter(system => system.status === 'failed').length;

      if (failedCount > 0) {
        // Use a single translated message that includes failedCount and total.
        this.notificationService.info(
          this.translate.instant('NOTIFICATIONS.INFO.INTEGRATIONS_ISSUES_STEP', {
            failedCount,
            total: relevantSystems.length,
            step
          })
        );
      } else {
        // Check if this was the final step. Do NOT automatically cleanup UI state here.
        // Let the component decide when to clear visuals, e.g., after the user clicks "Done".
        // Use a single translated message key for completed steps (including final step)
        this.notificationService.success(this.translate.instant('NOTIFICATIONS.SUCCESS.INTEGRATIONS_STEP_COMPLETE', { step }));
      }
    }
  }

  private unsubscribeFromIntegrationTopic(subscriptionKey: string): void {
    const subDetail = this.activeSubscriptions.get(subscriptionKey);
    if (subDetail) {
      subDetail.destroy$.next();
      subDetail.destroy$.complete();

      if (!subDetail.subscription.closed) {
        subDetail.subscription.unsubscribe();
      }

      this.integrationWebSocket.unsubscribeFromTopic(subDetail.topic as SubscriptionMessage['topic']);
      this.activeSubscriptions.delete(subscriptionKey);
    }
  }

  private cleanupSubscriptionsForStep(step: number, allConfigs: IntegrationConfig[]): void {
    const configsToClean = allConfigs.filter(config => config.step >= step);

    if (configsToClean.length === 0) {
      return;
    }

    configsToClean.forEach(config => {
      const subscriptionKey = `${config.name}_${config.step}`;
      this.unsubscribeFromIntegrationTopic(subscriptionKey);
    });
  }

  /**
   * Cleans up all active subscriptions and resets integration state
   * Called when all integration steps have completed successfully
   */
  public cleanupAllSubscriptions(): void {
    // Clean up all active subscriptions
    this.activeSubscriptions.forEach((_, key) => {
      this.unsubscribeFromIntegrationTopic(key);
    });

    // Do not clear integrationSystemsSignal here - keep last known statuses visible for the user.
    // The visible state will be reset explicitly when a new creation flow starts (step 1).
    // However, clear the primary item id as subscriptions are cleaned up and the flow is finished.
    this.primaryItemIdSignal.set(undefined);
    this.releasePreSubscribedTopics();
  }

  /**
   * Prepare the manager for a brand-new creation flow by clearing managed subscriptions
   * and resetting visible integration state before any UI binds to it.
   */
  public prepareForNewCreationFlow(): void {
    this.cleanupAllSubscriptions();
    this.integrationSystemsSignal.set([]);
    this.primaryItemIdSignal.set(undefined);
  }

  public getIntegrationSystems(): Signal<readonly BaseIntegrationSystem[]> {
    const systems = this.integrationSystemsSignal.asReadonly();
    return systems;
  }

  /**
   * Signal exposing the currently known primary item id (set when step1 integration returns it)
   */
  public primaryItemId(): Signal<string | undefined> {
    return this.primaryItemIdSignal.asReadonly();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    // Clean up all active subscriptions
    this.activeSubscriptions.forEach((_, key) => {
      this.unsubscribeFromIntegrationTopic(key);
    });

    this.releasePreSubscribedTopics();
  }

  /**
   * Reset internal integration state for a new creation flow.
   * Does not perform subscription cleanup here — that is handled separately.
   */
  private resetIntegrationState(): void {
    // Preserve prior subscription objects; just reset the visible statuses and primary id
    this.integrationSystemsSignal.set([]);
    this.primaryItemIdSignal.set(undefined);
  }

  /**
   * Send subscribe messages for all distinct topics found in the provided configs
   * (or the stored configs). This sends raw subscribe messages via WebSocketService
   * and does NOT create local managed subscriptions or later unsubscribe.
   */
  public preSubscribeAllTopics(integrationConfigs?: IntegrationConfig[]): void {
    const configs = integrationConfigs && integrationConfigs.length > 0 ? integrationConfigs : this.allIntegrationConfigs;
    if (!configs || configs.length === 0) {
      return;
    }

    const topics = Array.from(new Set(configs.map(cfg => cfg.topic)));

    const subscribeAll = () => {
      this.integrationWebSocket.subscribeToTopics(topics);
      topics.forEach(topic => this.preSubscribedTopics.add(topic));
    };

    if (this.webSocketService.isConnected()) {
      subscribeAll();
    } else {
      const sub = this.webSocketService.getConnectionStatus().subscribe(connected => {
        if (connected) {
          subscribeAll();
          sub.unsubscribe();
        }
      });
    }
  }

  private releasePreSubscribedTopics(): void {
    if (this.preSubscribedTopics.size === 0) {
      return;
    }

    this.preSubscribedTopics.forEach(topic => this.integrationWebSocket.unsubscribeFromTopic(topic));
    this.preSubscribedTopics.clear();
  }
}
